import { Button } from "@/components/core/buttons/Button";
import { cn } from "@/lib/utils";
import { IconBox } from "components/core/IconBox";
import { MixModal } from "components/core/modals/MixModal";
import { Mix_Content_Category_Enum } from "graphql/generated";
import { useAuth } from "hooks/useAuth";
import { fbqTrackCustom } from "lib/fpixel";
import { fireEvent } from "lib/ga";
import { Share2Icon } from "lucide-react";
import { useRouter } from "next/router";
import { ComponentProps, Fragment, HTMLAttributes, useState } from "react";
import { EXTENSION_STORE_URLS } from "utils/enum";

import { Text } from "../../core/Text";
import { ContentCTAButton, ProductCTAButton } from "./ContentCTAButton";

type ContentCTACardProps = {
    content: Mix_ContentFragment;
    contentInfo: Array<{ title: string; content: string }>;
    product?: ProductFragment | null;
    ctaText: string;
    onShareClick?: () => void;
    browserName?: string;
};

const ItemLabel = (
    props: Omit<ComponentProps<typeof Text>, "variant" | "color">
) => <Text variant="body3" color="text.secondary" {...props} />;

const ItemValue = (
    props: Omit<ComponentProps<typeof Text>, "variant" | "color">
) => <Text variant="body3" color="text.primary" {...props} />;

function CTACard({
    content,
    contentInfo,
    product,
    ctaText,
    className,
    ...props
}: Omit<HTMLAttributes<HTMLDivElement>, "content"> & ContentCTACardProps) {
    const router = useRouter();
    const { isLoggedIn, openSignInModal } = useAuth();

    return (
        <div
            className={cn("flex flex-col bg-common-white-main", className)}
            {...props}
        >
            <Text className="mb-3" variant="subtitle1">
                {content.title}
            </Text>
            <div
                className="mb-4 grid grid-cols-[max-content_1fr] gap-x-4
                    gap-y-2"
            >
                {contentInfo.map(({ title, content }) => (
                    <Fragment key={title}>
                        <ItemLabel>{title}</ItemLabel>
                        <ItemValue>{content}</ItemValue>
                    </Fragment>
                ))}
                {product && (
                    <Fragment>
                        <ItemLabel>금액</ItemLabel>
                        {product.on_discount ? (
                            <div className="flex items-center gap-2">
                                <ItemValue>
                                    {product.discount_price.toLocaleString()}원
                                </ItemValue>
                                <Text
                                    className="line-through"
                                    variant="caption2"
                                    color="text.placeholder"
                                >
                                    {product.price.toLocaleString()}원
                                </Text>
                            </div>
                        ) : (
                            <ItemValue>
                                {product.price.toLocaleString()}원
                            </ItemValue>
                        )}
                    </Fragment>
                )}
            </div>
            {product ? (
                <ProductCTAButton
                    product={product}
                    ctaText={ctaText}
                    disabled={!product.on_order}
                    disabledText="판매 종료"
                    onPaymentBtnClick={() => {
                        const gaEventParams = {
                            content_id: content.id,
                            content_url: content.originalurl,
                            content_title: content.title,
                            content_category: content.category,
                            product_id: product.id,
                            is_guest_payment: false
                        };

                        fbqTrackCustom("payment_button_click", {
                            ...gaEventParams
                        });

                        if (isLoggedIn) {
                            fireEvent({
                                event: "payment_start",
                                params: {
                                    ...gaEventParams
                                }
                            });

                            router.push(
                                `/payments/start?content_id=${content.id}&product_id=${product.id}&discount_apply=${product.on_discount}`
                            );
                        } else {
                            fireEvent({
                                event: "payment_button_click",
                                params: {
                                    ...gaEventParams
                                }
                            });

                            openSignInModal();
                        }
                    }}
                    onGuestPaymentBtnClick={() => {
                        const gaEventParams = {
                            content_id: content.id,
                            content_url: content.originalurl,
                            content_title: content.title,
                            content_category: content.category,
                            product_id: product.id,
                            is_guest_payment: true
                        };

                        fbqTrackCustom("payment_button_click", {
                            ...gaEventParams
                        });

                        fireEvent({
                            event: "payment_button_click",
                            params: {
                                ...gaEventParams
                            }
                        });

                        fireEvent({
                            event: "payment_start",
                            params: {
                                ...gaEventParams
                            }
                        });

                        router.push(
                            `/payments/start?content_id=${content.id}&product_id=${product.id}&discount_apply=${product.on_discount}`
                        );
                    }}
                />
            ) : content.category === Mix_Content_Category_Enum.Offline ? (
                <ContentCTAButton ctaText={ctaText} disabled={true} />
            ) : (
                content.category === Mix_Content_Category_Enum.Report && (
                    <ContentCTAButton
                        ctaText={ctaText}
                        disabled={!content.pdfurl}
                        onClick={() => {
                            const gaEventParams = {
                                content_id: content.id,
                                content_url: content.originalurl,
                                content_title: content.title
                            };

                            if (isLoggedIn) {
                                if (content.pdfurl) {
                                    fireEvent({
                                        event: "report_download",
                                        params: {
                                            ...gaEventParams
                                        }
                                    });

                                    window.open(content.pdfurl);
                                }
                            } else {
                                fireEvent({
                                    event: "report_button_click",
                                    params: {
                                        ...gaEventParams
                                    }
                                });

                                openSignInModal();
                            }
                        }}
                    />
                )
            )}
        </div>
    );
}

export function MobileContentCTACard(props: ContentCTACardProps) {
    const [showModal, setShowModal] = useState(false);

    return (
        <>
            <div
                className="sticky bottom-0 -mb-6 -ml-2 block space-y-3
                    bg-background py-6 md:hidden"
            >
                {props.product ? (
                    <ContentCTAButton
                        ctaText={props.ctaText}
                        disabled={!props.product.on_order}
                        disabledText="판매 종료"
                        onClick={() => setShowModal(true)}
                    />
                ) : props.content.category ===
                  Mix_Content_Category_Enum.Offline ? (
                    <ContentCTAButton
                        ctaText={props.ctaText}
                        disabled={true}
                        onClick={() => setShowModal(true)}
                    />
                ) : (
                    props.content.category ===
                        Mix_Content_Category_Enum.Report && (
                        <ContentCTAButton
                            ctaText={props.ctaText}
                            disabled={!props.content.pdfurl}
                            onClick={() => setShowModal(true)}
                        />
                    )
                )}
                {(props.onShareClick || props.browserName) && (
                    <div className="flex gap-2">
                        {props.onShareClick && (
                            <Button
                                variant="filled"
                                color="secondary"
                                className="flex-1 px-3 py-2"
                                onClick={props.onShareClick}
                            >
                                <Share2Icon className="mr-1 size-4" />
                                콘텐츠 공유하기
                            </Button>
                        )}
                        {props.browserName &&
                            !["Safari", "Opera", "Unknown"].includes(
                                props.browserName
                            ) && (
                                <Button
                                    variant="filled"
                                    className="flex-1 px-3 py-2"
                                    onClick={() => {
                                        window.open(
                                            EXTENSION_STORE_URLS[
                                                props.browserName!.toUpperCase()
                                            ]?.url,
                                            "_blank"
                                        );
                                    }}
                                >
                                    <IconBox size="medium" name="Add" />
                                    {props.browserName}에 추가하고 인사이트
                                    챙기기
                                </Button>
                            )}
                    </div>
                )}
            </div>
            <MixModal
                className="flex items-end md:hidden"
                isOpen={showModal}
                onRequestClose={() => setShowModal(false)}
            >
                <CTACard className="w-full rounded-t-2xl p-6" {...props} />
            </MixModal>
        </>
    );
}

export function DesktopContentCTACard(props: ContentCTACardProps) {
    return (
        <aside className="sticky top-40 hidden h-full shrink-0 md:block">
            <CTACard
                className="min-w-[240px] max-w-[260px] bg-background"
                {...props}
            />
        </aside>
    );
}
